import { createContext, useEffect, useState } from "react";
import api from "../api/axios";
import i18n, { initI18n } from "../i18n";
import { applyThemeFavicon } from "../utils/themeFavicon";
import { resolveThemeDomain, isDemoHostname } from "../utils/landingCompany";
//import { applyTenantPwaFromTheme } from "../utils/tenantPwa";

export const ThemeContext = createContext();

const DEFAULT_PAGE_TITLE = "iBottomLine";

function applyDocumentTitleFromTheme(themeData) {
  const name =
    themeData?.page_title?.trim() || themeData?.main_company_name?.trim();
  document.title = name || DEFAULT_PAGE_TITLE;
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(null);
  const [ready, setReady] = useState(false); // ✅ track when i18n is ready

  const CACHE_KEY = "theme";
  const CACHE_TIME_KEY = "theme_cache_time";
  const CACHE_DOMAIN_KEY = "theme_domain";
  const CACHE_DURATION = 100 * 60 * 1000;

  const fetchAndApplyTheme = async (domain) => {
    try {
      const now = Date.now();
      const { data } = await api.get(`/auth/theme/${domain}?ts=${now}`);
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
      localStorage.setItem(CACHE_TIME_KEY, now.toString());
      localStorage.setItem(CACHE_DOMAIN_KEY, domain);
      setTheme(data);
      applyDocumentTitleFromTheme(data);

      applyThemeFavicon(data?.favicon, {
        cacheBust: data?.updated_at ?? now,
      });
  //    applyTenantPwaFromTheme(data, {
    //    cacheBust: data?.updated_at ?? now,
   //   });

      const companyId = data?.id ?? 1;
      await initI18n(companyId, true);
      await i18n.changeLanguage("en");
      setReady(true); // ✅ translation ready
    } catch (err) {
      console.error("Theme fetch error:", err);
      document.title = DEFAULT_PAGE_TITLE;
      setReady(true); // fail gracefully
    }
  };

  useEffect(() => {
    const domain = resolveThemeDomain(window.location.pathname);
    const cachedTheme = localStorage.getItem(CACHE_KEY);
    const cachedTime = localStorage.getItem(CACHE_TIME_KEY);
    const cachedDomain = localStorage.getItem(CACHE_DOMAIN_KEY);
    const now = Date.now();

    // The demo/guest host always shows whatever company is currently assigned
    // to it, which can change at any time via the admin CSS/logo/theme
    // upload — a 100-minute stale cache would mask that update for anyone
    // who tested the demo host recently in this same browser. Skip the
    // cache entirely there instead of shortening the window for everyone.
    const isCacheValid =
      !isDemoHostname() &&
      cachedTheme &&
      cachedTime &&
      cachedDomain === domain &&
      now - parseInt(cachedTime, 10) < CACHE_DURATION;

    if (isCacheValid) {
      const themeData = JSON.parse(cachedTheme);
      setTheme(themeData);
      applyDocumentTitleFromTheme(themeData);
      applyThemeFavicon(themeData?.favicon, {
        cacheBust: themeData?.updated_at ?? parseInt(cachedTime, 10),
      });
  //    applyTenantPwaFromTheme(themeData, {
    //    cacheBust: themeData?.updated_at ?? parseInt(cachedTime, 10),
   //   });
      const companyId = themeData?.id ?? 1;
      initI18n(companyId).then(() => {
        i18n.changeLanguage("en").then(() => setReady(true));
      });
    } else {
      fetchAndApplyTheme(domain);
    }

    const interval = setInterval(() => fetchAndApplyTheme(domain), CACHE_DURATION);
    return () => clearInterval(interval);
  // CACHE_* and fetchAndApplyTheme are stable; run only on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!theme || !ready)
    return (
      <div className="flex justify-center items-center h-screen">
        Loading...
      </div>
    );

  return <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>;
}
